import React, { FunctionComponent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { PackagePartItem } from "../../../queries/useSearchPartPackagesByVehicle";
import { usePackageEditor } from "../../../stores/hooks/usePackageEditor";
import { Dialog } from "../../../ui/Dialog/Dialog";
import { DialogHeader } from "../../../ui/Dialog/DialogHeader";
import { TabPanels, TabPanelsContext } from "../../../ui/TabPanels/TabPanels";
import { createVehicleMappingKey } from "../createVehicleMappingKey";
import { useVehicleMaping } from "../useVehicleMaping";
import { useVehicleMappingCriteria } from "../useVehicleMappingCriteria";
import { EditPackageTabs } from "./EditPackageTabs";
import { Footer } from "./Footer";
import { PackageInfo } from "./PackageInfo";
import { PackageParts } from "./PackageParts";
import { Vehicles } from "./Vehicles";

export const EditPackageDialog: FunctionComponent<{
  onClose: VoidFunction;
  selectedPackage?: PackagePartItem;
}> = ({ onClose, selectedPackage }) => {
  const { t } = useTranslation();
  const tabsContextRef = useRef<TabPanelsContext>();
  const [isSending, setIsSending] = useState(false);

  const [
    packageState,
    {
      getPartQuantity,
      setForceVehicleLoaderMode,
      setModelsAreLoading,
      setQuantity,
      setTabIndex,
      setVehicleManufacturers,
      setVehicleMapping,
      toggleVehicleManufacturerId,
      toggleVehicleYearId,
    },
  ] = usePackageEditor(selectedPackage?.partDetails.primaryPart.itemId || null);

  useEffect(() => {
    if (
      tabsContextRef.current &&
      packageState.tabIndex !== tabsContextRef.current.index
    ) {
      tabsContextRef.current.setIndex(packageState.tabIndex);
    }
  }, [packageState.tabIndex]);

  const [vehicleMappingCriteria, setVehicleMappingCriteria] =
    useVehicleMappingCriteria();

  const fallbackVehicleMapping =
    (selectedPackage?.partSummary.primaryPart.vehicleIdList
      .map((vehicleId) => {
        const details = selectedPackage.partSummary.primaryPart.vehicleDetails;
        const vehicle = details[String(vehicleId)];
        if (!vehicle) {
          return null;
        }
        const vehicleKey = createVehicleMappingKey(vehicle);
        return vehicleKey;
      })
      .filter(Boolean) as string[] | undefined) || [];

  const data = useVehicleMaping({
    criteria: vehicleMappingCriteria,
    fallbackManufacturers: packageState.vehicleManufacturers.filter((item) => {
      const formattedTiresize =
        selectedPackage?.partTire?.partDetails.primaryPart.formattedTiresize;
      return formattedTiresize
        ? item.vehicleSubmodelTiresizeArray.some(
            (tireSizeItem) =>
              tireSizeItem.formattedTireSize === formattedTiresize
          )
        : true;
    }),
    partId: selectedPackage?.partSummary.primaryPart.itemId ?? null,
    setVehicleMapping,
    vehicleDetails:
      selectedPackage?.partSummary.primaryPart.vehicleDetails ?? null,
    vehicleMapping: packageState.vehicleMapping ?? fallbackVehicleMapping,
  });

  return (
    <Dialog
      className="md:w-[32rem]"
      id="edit-package-dialog"
      onClose={onClose}
      placement="top"
      visible={Boolean(selectedPackage)}
    >
      <DialogHeader onClose={onClose} title={t("packageEditor.editPackage")} />
      <EditPackageTabs
        selectTab={setTabIndex}
        tabIndex={packageState.tabIndex}
      />
      <TabPanels
        className="pb-4 pt-2"
        contextRef={tabsContextRef}
        defaultIndex={0}
      >
        <PackageParts
          getPartQuantity={getPartQuantity}
          selectedPackage={selectedPackage}
          setQuantity={setQuantity}
        />
        <Vehicles
          data={data}
          forceVehicleLoaderMode={packageState.forceVehicleLoaderMode}
          setForceVehicleLoaderMode={setForceVehicleLoaderMode}
          setModelsAreLoading={setModelsAreLoading}
          setVehicleManufacturers={setVehicleManufacturers}
          setVehicleMappingCriteria={setVehicleMappingCriteria}
          toggleVehicleManufacturerId={toggleVehicleManufacturerId}
          toggleVehicleYearId={toggleVehicleYearId}
          vehicleManufacturerIds={packageState.vehicleManufacturerIds}
          vehicleMappingCriteria={vehicleMappingCriteria}
          vehicleYearIds={packageState.vehicleYearIds}
        />
        <PackageInfo
          getPartQuantity={getPartQuantity}
          mappedIds={data.selectedVehicleIds}
          onUpdate={onClose}
          selectedPackage={selectedPackage}
          setIsSending={setIsSending}
        />
      </TabPanels>
      <Footer
        shoulDisableFetchVehiclesButton={
          !packageState.vehicleYearIds.length ||
          !packageState.vehicleManufacturerIds.length
        }
        forceVehicleLoaderMode={packageState.forceVehicleLoaderMode}
        hide={onClose}
        isSending={isSending}
        modelsAreLoading={packageState.modelsAreLoading}
        selectTab={setTabIndex}
        setForceVehicleLoaderMode={setForceVehicleLoaderMode}
        tabIndex={packageState.tabIndex}
      />
    </Dialog>
  );
};
